import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { Button, Form, Alert, Card, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import ReactTable from "@components/ReactTable";
import { lookupSensorByTui, writeSensorEvent, unassignSensor } from "../services/sensorCrud";
import { sensorState, haveSame18Prefix } from "./sensorUtils";

export default function SensorTab({ searchTui, setSearchTui }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const timeString =
    lang === "de" ? "de-DE" : lang === "en" ? "en-UK" : lang === "it" ? "it-IT" : "de-DE";

  const [tuiInput, setTuiInput] = useState(searchTui || "");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState([]);
  const [qcPassed, setQcPassed] = useState(null);
  const [comments, setComments] = useState("");

  const {
    data: sensorData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(["sensorLookup", { tui: searchTui }], () => lookupSensorByTui(searchTui), {
    enabled: !!searchTui,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (error?.response?.status === 400) {
      const matches = error.response.data?.matching_tuis || error.response.data?.matches || [];
      if (Array.isArray(matches) && matches.length > 1) {
        const prefix = haveSame18Prefix(matches);
        if (prefix) {
          setSearchTui(matches[0]);
        }
      }
    }
  }, [error, setSearchTui]);

  useEffect(() => {
    setTuiInput(searchTui || "");
  }, [searchTui]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!tuiInput.trim()) return;
    setSearchTui(tuiInput.trim());
    setPage(1);
    refetch();
  };

  async function writeEventForAll(sensorIdentifier, payload) {
    try {
      await writeSensorEvent({ ...payload, tuiPart: sensorIdentifier });
    } catch (err) {
      const matches = err?.response?.data?.matching_tuis;
      const prefix = haveSame18Prefix(matches);
      if (matches && prefix) {
        await Promise.all(matches.map((tui) => writeSensorEvent({ ...payload, tuiPart: tui })));
      } else {
        throw err;
      }
    }
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!sensorData) return;

    const identifier =
      sensorData.network_type === "LW" ? sensorData.hardware_serial : sensorData.imei;

    const payload = {
      comment: comments,
      usage: "",
    };
    if (qcPassed !== null) payload.qc = qcPassed;

    try {
      await writeEventForAll(identifier, payload);
      refetch();
    } catch (err) {
      console.error("Failed to write sensor event:", err);
    }
  };

  const renderError = () => {
    if (error?.response?.status === 404) {
      return (
        <Alert variant="warning">
          <strong>{t("sensor_not_found")}</strong>
          <br />
          {t("sensor_not_found_message")}
        </Alert>
      );
    }

    if (error?.response?.status === 400) {
      const matches = error.response.data?.matching_tuis || error.response.data?.matches || [];
      if (Array.isArray(matches) && matches.length > 1) {
        return (
          <Alert variant="warning">
            <strong>{t("multiple_sensors_found")}</strong>
            <br />
            {t("multiple_sensors_found_message")}:
            <ul className="mb-0 mt-2">
              {matches.map((tui, idx) => (
                <li key={idx}>
                  <Button
                    variant="link"
                    className="p-0 text-decoration-underline"
                    onClick={() => setSearchTui(tui)}
                  >
                    {tui}
                  </Button>
                </li>
              ))}
            </ul>
          </Alert>
        );
      }
    }

    return <ServerError />;
  };

  const sensorEventsColumns = useMemo(
    () => [
      {
        Header: t("time"),
        accessor: "timestamp",
        Cell: ({ value }) =>
          new Date(value).toLocaleString(timeString, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
      },
      {
        Header: t("qc_passed"),
        accessor: "qc_passed",
        Cell: ({ value }) => {
          if (value === true) {
            return <span className="badge bg-success">{t("passed")}</span>;
          }
          if (value === false) {
            return <span className="badge bg-danger">{t("failed")}</span>;
          }
          return "-";
        },
      },
      {
        Header: t("comments"),
        accessor: "comments",
        Cell: ({ value }) => value || "-",
      },
    ],
    [t, timeString]
  );

  const renderSensorInfo = () => {
    if (!sensorData) return null;

    const sensor = sensorData;
    const sensor_events = sensorData?.sensor_events || [];
    const pageSize = 20;

    const sortedEvents = [...sensor_events];
    if (sortBy.length > 0) {
      const { id, desc } = sortBy[0];
      sortedEvents.sort((a, b) => {
        let valA = a[id];
        let valB = b[id];

        if (id === "timestamp") {
          valA = new Date(valA).getTime();
          valB = new Date(valB).getTime();
        }

        if (valA === valB) return 0;
        const comp = valA > valB ? 1 : -1;
        return desc ? -comp : comp;
      });
    }

    const pageCount = Math.ceil(sortedEvents.length / pageSize);
    const paginatedEvents = sortedEvents.slice((page - 1) * pageSize, page * pageSize);

    return (
      <div className="mt-4">
        <Card>
          <Card.Header>
            <h5 className="mb-0">{sensor.name}</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <ul className="list-unstyled">
                  <li className="pb-2">
                    <strong>{t("tui")}:</strong> {sensor.tui}
                  </li>
                  <li className="pb-2">
                    <strong>
                      {sensor.network_type === "LW" ? t("hardware_serial") : t("imei")}:
                    </strong>{" "}
                    {sensor.network_type === "LW" ? sensor.hardware_serial : sensor.imei}
                  </li>
                  {sensor.network_type === "LW" && (
                    <li className="pb-2">
                      <strong>{t("join_eui")}:</strong> {sensor.join_eui}
                    </li>
                  )}
                  {sensor.network_type === "LW" && (
                    <li className="pb-2">
                      <strong>{t("app_key")}:</strong> {sensor.app_key}
                    </li>
                  )}
                  {sensor.password != null && (
                    <li className="pb-2">
                      <strong>{t("password")}:</strong> {sensor.password}
                    </li>
                  )}

                  <li className="pb-2">
                    <strong>{t("sensor_type")}:</strong> {sensor.sensor_type || t("unknown")}
                  </li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="list-unstyled">
                  <li className="pb-2">
                    <strong>{t("network_type")}:</strong> {sensor.network_type || t("unknown")}
                  </li>
                  <li className="pb-2">
                    <strong>{t("last_battery")}:</strong>{" "}
                    {sensor.battery ? sensor.battery + "%" : t("unknown")}
                  </li>
                  <li className="pb-2">
                    <strong>{t("sensor_state")}:</strong>{" "}
                    {t(sensorState[sensor.sensor_state] || sensorState[4])}
                  </li>
                  {sensor.amount_nodes != null ? (
                    <li className="pb-2">
                      <strong>Probes:</strong> {sensor.amount_nodes}
                    </li>
                  ) : (
                    sensor.pulse_sensor !== null && (
                      <li className="pb-2">
                        <strong>{t("threshold")}:</strong> {sensor.threshold}
                      </li>
                    )
                  )}
                  {sensor.created_at != null && (
                    <li className="pb-2">
                      <strong>{t("created_at")}:</strong>{" "}
                      {moment(sensor.created_at).format("DD.MM.YYYY HH:mm")}
                    </li>
                  )}
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="mt-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t("update_sensor_values")}</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSave}>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("qc_passed")}</Form.Label>
                      <Form.Select
                        value={qcPassed === null ? "" : qcPassed ? "true" : "false"}
                        onChange={(e) => {
                          const v = e.target.value;
                          setQcPassed(v === "" ? null : v === "true");
                        }}
                      >
                        <option value="">{t("no_change")}</option>
                        <option value="true">{t("passed")}</option>
                        <option value="false">{t("failed")}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("comments")}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t("enter_comments")}
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button type="submit" variant="primary">
                  {t("save")}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>

        <div className="mt-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t("sensor_return_title")}</h5>
            </Card.Header>
            <Card.Body>
              {sensor.current_customer ? (
                <>
                  <Alert variant="info" className="mb-0 p-0 pb-2">
                    <strong>{t("currently_assigned_to")}:</strong> {sensor.current_customer.name}
                    {sensor.current_assignment_date && (
                      <>
                        <br />
                        <small>
                          {t("assigned_since")}:{" "}
                          {moment(sensor.current_assignment_date).format("DD.MM.YYYY")}
                        </small>
                      </>
                    )}
                  </Alert>
                  {/* <p className="text-muted mb-1">{t("sensor_return_description")}</p> */}
                  <Form
                    onSubmit={async (e) => {
                      e.preventDefault();

                      if (!sensorData) return;

                      const identifier =
                        sensorData.network_type === "LW"
                          ? sensorData.hardware_serial
                          : sensorData.imei;

                      const returnDateValue =
                        e.target.returnDate.value || new Date().toISOString().split("T")[0];

                      try {
                        await unassignSensor({
                          tuiPart: identifier,
                          unassignedAt: returnDateValue,
                        });

                        await writeSensorEvent({
                          tuiPart: identifier,
                          comment: "Returned to office",
                        });

                        refetch();
                        e.target.reset();
                        alert(t("sensor_returned_successfully"));
                      } catch (err) {
                        console.error("Failed to return sensor:", err);

                        let errorMessage = t("sensor_return_failed");
                        if (err?.response?.data) {
                          if (typeof err.response.data === "string") {
                            errorMessage = err.response.data;
                          } else if (err.response.data.message) {
                            errorMessage = err.response.data.message;
                          } else if (err.response.data.detail) {
                            errorMessage = err.response.data.detail;
                          } else if (err.response.data.error) {
                            errorMessage = err.response.data.error;
                          }
                        }

                        alert(`${t("sensor_return_failed")}: ${errorMessage}`);
                      }
                    }}
                  >
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>{t("return_date")}</Form.Label>
                          <div className="d-flex gap-2 align-items-start">
                            <div>
                              <Form.Control type="date" name="returnDate" />
                              <Form.Text className="text-muted">
                                {t("return_date_help_default_today")}
                              </Form.Text>
                            </div>
                            <Button
                              type="submit"
                              variant="warning"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {t("unassign_sensor")}
                            </Button>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </>
              ) : (
                <Alert variant="secondary">{t("sensor_not_assigned_to_customer")}</Alert>
              )}
            </Card.Body>
          </Card>
        </div>

        {sensor_events && sensor_events.length > 0 ? (
          <div className="mt-4">
            <h5>
              {t("sensor_events")} ({sensor_events.length})
            </h5>
            <ReactTable
              key={`events-table-${sensor_events.length}`}
              columns={sensorEventsColumns}
              data={paginatedEvents}
              activeId={0}
              pagesize={pageSize}
              manualPagination
              manualSortBy
              onSortChange={setSortBy}
              pageCount={pageCount}
              currentPage={page}
              onPageChange={(newPage) => setPage(newPage)}
              compact={true}
              filter={false}
            />
          </div>
        ) : (
          <div className="mt-4">
            <Alert variant="info">{t("no_sensor_events_found")}</Alert>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <Card.Body>
        <Form onSubmit={handleSearch}>
          <Row>
            <Col md={8}>
              <div className="h-100 d-flex align-items-center">
                <Form.Group className="w-100">
                  <Form.Label>{t("tui_part")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("enter_tui_part_placeholder")}
                    value={tuiInput}
                    onChange={(e) => setTuiInput(e.target.value)}
                    disabled={isLoading}
                    className="mb-1"
                  />
                  <Form.Text className="text-muted">{t("tui_search_help")}</Form.Text>
                </Form.Group>
              </div>
            </Col>
            <Col md={4}>
              <div className="h-100 d-flex align-items-center">
                <Button
                  type="submit"
                  variant="primary"
                  // className=""
                  disabled={isLoading || !tuiInput.trim()}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("searching")}
                    </>
                  ) : (
                    t("search")
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>

        {isError && renderError()}
        {sensorData && renderSensorInfo()}
      </Card.Body>
    </Card>
  );
}
